import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { useCallback, useRef } from 'react';

/**
 * 积分商城页面埋点 Hook
 */
export function usePointsMallTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const enterTimer = useRef<number>(Date.now());

  /**
   * 神策埋点：积分商城页面浏览
   * @TrackType sensorsdata
   * @EventType PointsMallPage_View
   * @EventDesc 积分商城页面浏览
   * @EventParams {
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPointsMallView = useCallback(() => {
    try {
      enterTimer.current = Date.now();
      const { refer_page_name, refer_url_path } = getFromRouterInfo();

      trackEvent(
        {
          eventName: 'PointsMallPage_View',
          properties: {
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('PointsMallPage_View tracking error:', error);
    }
  }, [trackEvent, getFromRouterInfo]);

  /**
   * 神策埋点：离开积分商城页面
   * @TrackType sensorsdata
   * @EventType PointsMallPage_Leave
   * @EventDesc 离开积分商城页面
   * @EventParams {
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   *   view_duration: 浏览时长(秒),
   * }
   */
  const trackPointsMallLeave = useCallback(
    (_fromRouterInfo?: any) => {
      try {
        const viewDuration = Math.ceil((Date.now() - enterTimer.current) / 1000);
        const routerInfo = _fromRouterInfo || getFromRouterInfo();
        const { refer_page_name, refer_url_path } = routerInfo;

        trackEvent(
          {
            eventName: 'PointsMallPage_Leave',
            properties: {
              refer_page_name,
              refer_url_path,
              view_duration: viewDuration,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('PointsMallPage_Leave tracking error:', error);
      }
    },
    [trackEvent, getFromRouterInfo],
  );

  return {
    trackPointsMallView,
    trackPointsMallLeave,
  };
}
